import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { rentalBookingService, RentalBookingDTO } from '../../../../services/rentalBookingService';
import { XCircle, CheckCircle2, CalendarRange, CirclePlus, RefreshCw, Clock } from 'lucide-react';
import '../CarRentalPage.css';

const BookingListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<RentalBookingDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unwrap = (res: any): RentalBookingDTO[] => {
    const d = res?.data?.data;
    return Array.isArray(d) ? d : (d?.data ?? []);
  };

  const load = async (spinner: 'loading' | 'refreshing' = 'loading') => {
    spinner === 'loading' ? setLoading(true) : setRefreshing(true);
    setError(null);
    try {
      const res = await rentalBookingService.getAll();
      setItems(unwrap(res));
    } catch {
      setError('Failed to load bookings');
    } finally {
      spinner === 'loading' ? setLoading(false) : setRefreshing(false);
    }
  };

  useEffect(() => { load('loading'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = async (row: RentalBookingDTO) => {
    if (!window.confirm(`Cancel booking ${row.bookingNumber}?`)) return;
    await rentalBookingService.cancel(row.id);
    load('refreshing');
  };

  const handleConfirm = async (row: RentalBookingDTO) => {
    await rentalBookingService.confirm(row.id);
    load('refreshing');
  };

  const confirmedCount = items.filter((i) => i.status === 'Confirmed').length;
  const activeCount    = items.filter((i) => i.status === 'Active').length;
  const pendingCount   = items.filter((i) => i.status === 'PendingPayment' || i.status === 'Draft').length;

  const columns: GridColumn[] = [
    { key: 'bookingNumber', label: 'Booking #', width: '150px' },
    { key: 'customerName', label: 'Customer' },
    { key: 'vehicleLabel', label: 'Vehicle' },
    { key: 'pickupDate', label: 'Pickup', width: '150px', render: (v) => new Date(v).toLocaleString() },
    { key: 'returnDate', label: 'Return', width: '150px', render: (v) => new Date(v).toLocaleString() },
    { key: 'totalAmount', label: 'Amount', width: '100px', render: (v) => `$${Number(v).toFixed(2)}` },
    { key: 'status', label: 'Status', width: '130px', render: (v) => <span className={`ims-badge ims-badge-${String(v).toLowerCase()}`}>{v}</span> },
  ];

  const actions: GridAction[] = [
    { icon: <CheckCircle2 size={18} />, onClick: handleConfirm, className: 'grid-action-btn-view', title: 'Confirm', hidden: (row) => row.status !== 'PendingPayment' && row.status !== 'Draft' },
    { icon: <XCircle size={18} />, onClick: handleCancel, className: 'grid-action-btn-delete', title: 'Cancel', hidden: (row) => ['Completed', 'Cancelled', 'NoShow'].includes(row.status) },
  ];

  return (
    <Layout>
      <div className="ims-page car-rental-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">Rental Bookings</h1>
            <p className="ims-list-subtitle">Track and manage all vehicle rentals</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={() => load('refreshing')} disabled={refreshing}>
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="btn-new" onClick={() => navigate('/car-rental/schedule')}>
              <CirclePlus size={16} strokeWidth={1.75} /> New Booking
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-fleet"><CalendarRange size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">Total Bookings</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-booked"><CalendarRange size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{confirmedCount}</span>
              <span className="ims-stat-label">Confirmed</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-avail"><CalendarRange size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">Active</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-pending"><Clock size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{pendingCount}</span>
              <span className="ims-stat-label">Pending</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid columns={columns} actions={actions} data={items} loading={loading} searchPlaceholder="Search bookings..." />
        </div>
      </div>
    </Layout>
  );
};

export default BookingListPage;
